import type {
  Plan,
  Stage,
  PlannerInsights,
  PlannerInsightsSummary,
  PlannerInsightsTimeline,
  PlannerInsightsUpdates,
  PlannerInsightsAI,
  PlannerDashboardPlanSummary,
  PlannerDashboardHighlights
} from '@my-roadtrip/types'

type TimelineEvent = PlannerInsightsTimeline['nextStages'][number]

interface TimeBoundaries {
  now: number
  startOfMonth: number
  thirtyDaysAgo: number
}

interface InsightsAccumulator {
  timeline: PlannerInsights['timeline']
  activeDays: Set<string>
  lastPlanCreatedAt?: string
  lastPlanUpdatedAt?: string
  lastGeneratedAt?: string
  generatedPast30d: number
  aiPlans: number
  distanceTotal: number
  distanceSamples: number
  durationTotalDays: number
  durationSamples: number
  sharedPlans: number
  collaborativePlans: number
  upcomingPlans: number
  activePlans: number
  draftPlans: number
  publicPlans: number
  totalStages: number
  uniqueCollaborators: Set<string>
}

export function buildPlannerInsights(plans: Plan[]): PlannerInsights {
  const bounds = createTimeBoundaries()
  const acc = createInsightsAccumulator()

  for (const plan of plans) {
    accumulatePlanInsights(plan, acc, bounds)
  }

  return finalizeInsights(plans.length, acc)
}

export function buildDashboardHighlights(insights: PlannerInsights): PlannerDashboardHighlights {
  return {
    totalPlans: insights.summary.totalPlans,
    upcomingPlans: insights.summary.upcomingPlans,
    aiPlans: insights.summary.aiPlans
  }
}

export function buildPlanSummaries(plans: Plan[], limit = 5): PlannerDashboardPlanSummary[] {
  return plans
    .map(plan => toDashboardPlanSummary(plan))
    .sort((a, b) => {
      const aTime = Date.parse(a.updatedAt ?? '') || 0
      const bTime = Date.parse(b.updatedAt ?? '') || 0
      return bTime - aTime
    })
    .slice(0, limit)
}

export function selectSpotlightPlan(plans: Plan[]): PlannerDashboardPlanSummary | null {
  if (!plans.length) {
    return null
  }

  const withUpcoming = plans
    .map(plan => ({ plan, bounds: getPlanTemporalBounds(plan) }))
    .filter(({ bounds }) => bounds.start && bounds.start.getTime() >= Date.now())
    .sort((a, b) => (a.bounds.start!.getTime() - b.bounds.start!.getTime()))

  if (withUpcoming.length > 0) {
    return toDashboardPlanSummary(withUpcoming[0].plan)
  }

  const sorted = [...plans].sort((a, b) => {
    const aTime = normalizeDate(a.updatedAt)?.getTime() ?? normalizeDate(a.createdAt)?.getTime() ?? 0
    const bTime = normalizeDate(b.updatedAt)?.getTime() ?? normalizeDate(b.createdAt)?.getTime() ?? 0
    return bTime - aTime
  })

  return sorted.length ? toDashboardPlanSummary(sorted[0]) : null
}

function createTimeBoundaries(): TimeBoundaries {
  const now = Date.now()
  const nowDate = new Date(now)
  return {
    now,
    startOfMonth: new Date(nowDate.getFullYear(), nowDate.getMonth(), 1).getTime(),
    thirtyDaysAgo: now - 30 * 24 * 60 * 60 * 1000
  }
}

function createInsightsAccumulator(): InsightsAccumulator {
  return {
    timeline: {
      nextStages: [],
      overdueStages: []
    },
    activeDays: new Set<string>(),
    generatedPast30d: 0,
    aiPlans: 0,
    distanceTotal: 0,
    distanceSamples: 0,
    durationTotalDays: 0,
    durationSamples: 0,
    sharedPlans: 0,
    collaborativePlans: 0,
    upcomingPlans: 0,
    activePlans: 0,
    draftPlans: 0,
    publicPlans: 0,
    totalStages: 0,
    uniqueCollaborators: new Set<string>()
  }
}

function accumulatePlanInsights(plan: Plan, acc: InsightsAccumulator, bounds: TimeBoundaries): void {
  const stages = Array.isArray(plan.stages) ? plan.stages : []
  acc.totalStages += stages.length

  recordPlanDates(plan, acc, bounds)
  recordPlanRoute(plan, acc)
  recordPlanSharing(plan, acc)
  recordPlanCollaborators(plan, acc)
  recordPlanAI(plan, acc, bounds)

  const { hasUpcoming, hasCompleted } = accumulateStageEvents(plan, stages, acc, bounds)

  if (stages.length === 0) {
    acc.draftPlans += 1
    return
  }

  if (hasUpcoming && hasCompleted) {
    acc.activePlans += 1
  } else if (hasUpcoming) {
    acc.upcomingPlans += 1
  } else if (hasCompleted) {
    acc.activePlans += 1
  } else {
    acc.draftPlans += 1
  }
}

function recordPlanDates(plan: Plan, acc: InsightsAccumulator, bounds: TimeBoundaries): void {
  const createdAt = normalizeDate(plan.createdAt)
  if (createdAt) {
    const stamp = createdAt.toISOString()
    if (!acc.lastPlanCreatedAt || stamp > acc.lastPlanCreatedAt) {
      acc.lastPlanCreatedAt = stamp
    }
    if (createdAt.getTime() >= bounds.startOfMonth) {
      acc.activeDays.add(stamp.slice(0, 10))
    }
  }

  const updatedAt = normalizeDate(plan.updatedAt)
  if (updatedAt) {
    const stamp = updatedAt.toISOString()
    if (!acc.lastPlanUpdatedAt || stamp > acc.lastPlanUpdatedAt) {
      acc.lastPlanUpdatedAt = stamp
    }
    if (updatedAt.getTime() >= bounds.startOfMonth) {
      acc.activeDays.add(stamp.slice(0, 10))
    }
  }
}

function recordPlanRoute(plan: Plan, acc: InsightsAccumulator): void {
  const distance =
    typeof plan.routeData?.distance === 'number'
      ? plan.routeData.distance
      : typeof (plan.routeData as any)?.distanceKm === 'number'
        ? (plan.routeData as any).distanceKm
        : undefined

  if (typeof distance === 'number' && Number.isFinite(distance)) {
    acc.distanceTotal += distance
    acc.distanceSamples += 1
  }

  const { start, end } = getPlanTemporalBounds(plan)
  if (start && end && end.getTime() >= start.getTime()) {
    const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
    acc.durationTotalDays += diffDays
    acc.durationSamples += 1
  }
}

function recordPlanSharing(plan: Plan, acc: InsightsAccumulator): void {
  const sharedWith = Array.isArray(plan.sharedWith) ? plan.sharedWith : []
  const permissions = plan.sharedPermissions ? Object.keys(plan.sharedPermissions) : []
  const isShared = sharedWith.length > 0 || permissions.length > 0 || Boolean(plan.shareLink)
  if (isShared) {
    acc.sharedPlans += 1
  }

  if (plan.isPublic || plan.visibility === 'public') {
    acc.publicPlans += 1
  }
}

function recordPlanCollaborators(plan: Plan, acc: InsightsAccumulator): void {
  const collaborators = Array.isArray(plan.collaborators) ? plan.collaborators : []
  for (const collaborator of collaborators) {
    if (collaborator?.userId) {
      acc.uniqueCollaborators.add(collaborator.userId)
    }
  }
  if (collaborators.length > 1) {
    acc.collaborativePlans += 1
  }
}

function recordPlanAI(plan: Plan, acc: InsightsAccumulator, bounds: TimeBoundaries): void {
  const aiMetadata = plan.aiMetadata
  if (!aiMetadata) {
    return
  }

  acc.aiPlans += 1

  const generatedAt = normalizeDate((aiMetadata as any).generatedAt)
  if (!generatedAt) {
    return
  }

  const stamp = generatedAt.toISOString()
  if (!acc.lastGeneratedAt || stamp > acc.lastGeneratedAt) {
    acc.lastGeneratedAt = stamp
  }

  if (generatedAt.getTime() >= bounds.thirtyDaysAgo) {
    acc.generatedPast30d += 1
  }
}

function accumulateStageEvents(
  plan: Plan,
  stages: Stage[],
  acc: InsightsAccumulator,
  bounds: TimeBoundaries
): { hasUpcoming: boolean; hasCompleted: boolean } {
  let hasUpcoming = false
  let hasCompleted = false

  for (const stage of stages) {
    const arrival = normalizeDate(stage.estimatedArrival)
    const departure = normalizeDate(stage.estimatedDeparture)
    const fallback = normalizeDate(stage.createdAt)
    const occursAt = arrival ?? departure ?? fallback

    if (occursAt) {
      const event = createTimelineEvent(plan, stage, occursAt, resolveEventType(arrival, departure))
      if (occursAt.getTime() >= bounds.now) {
        hasUpcoming = true
        acc.timeline.nextStages.push(event)
      } else {
        hasCompleted = true
        acc.timeline.overdueStages.push(event)
      }
    }

    const stageUpdated = normalizeDate(stage.updatedAt)
    if (stageUpdated && stageUpdated.getTime() >= bounds.startOfMonth) {
      acc.activeDays.add(stageUpdated.toISOString().slice(0, 10))
    }
  }

  return { hasUpcoming, hasCompleted }
}

function createTimelineEvent(
  plan: Plan,
  stage: Stage,
  occursAt: Date,
  type: TimelineEvent['type']
): TimelineEvent {
  return {
    id: `${plan.id}:${stage.id}`,
    planId: plan.id,
    planName: plan.name,
    stageId: stage.id,
    stageName: stage.name,
    stageType: stage.type,
    occursAt: occursAt.toISOString(),
    type
  }
}

function resolveEventType(arrival: Date | null, departure: Date | null): TimelineEvent['type'] {
  if (arrival) {
    return 'arrival'
  }
  if (departure) {
    return 'departure'
  }
  return 'milestone'
}

function finalizeInsights(planCount: number, acc: InsightsAccumulator): PlannerInsights {
  sortTimeline(acc.timeline)

  const summary: PlannerInsightsSummary = {
    totalPlans: planCount,
    totalStages: acc.totalStages,
    upcomingPlans: acc.upcomingPlans,
    activePlans: acc.activePlans,
    draftPlans: acc.draftPlans,
    aiPlans: acc.aiPlans,
    sharedPlans: acc.sharedPlans,
    collaborativePlans: acc.collaborativePlans,
    uniqueCollaborators: acc.uniqueCollaborators.size,
    publicPlans: acc.publicPlans,
    averageTripDistanceKm: acc.distanceSamples > 0 ? Number((acc.distanceTotal / acc.distanceSamples).toFixed(2)) : 0,
    averageTripDurationDays: acc.durationSamples > 0 ? Number((acc.durationTotalDays / acc.durationSamples).toFixed(2)) : 0,
    totalDistanceKm: Number(acc.distanceTotal.toFixed(2))
  }

  const updates: PlannerInsightsUpdates = {
    activeDaysThisMonth: acc.activeDays.size,
    lastPlanCreatedAt: acc.lastPlanCreatedAt,
    lastPlanUpdatedAt: acc.lastPlanUpdatedAt
  }

  const adoptionRate = planCount > 0 ? Number(((acc.aiPlans / planCount) * 100).toFixed(1)) : 0
  const ai: PlannerInsightsAI = {
    adoptionRate,
    generatedPast30d: acc.generatedPast30d,
    lastGeneratedAt: acc.lastGeneratedAt
  }

  return {
    summary,
    timeline: acc.timeline,
    updates,
    ai,
    updatedAt: new Date().toISOString()
  }
}

function sortTimeline(timeline: PlannerInsights['timeline']): void {
  timeline.nextStages.sort((a, b) => Date.parse(a.occursAt) - Date.parse(b.occursAt))
  timeline.overdueStages.sort((a, b) => Date.parse(b.occursAt) - Date.parse(a.occursAt))
}

function normalizeDate(value: unknown): Date | null {
  if (!value) {
    return null
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }

  if (value && typeof (value as { toDate?: () => Date }).toDate === 'function') {
    try {
      const converted = (value as { toDate: () => Date }).toDate()
      if (converted instanceof Date && !Number.isNaN(converted.getTime())) {
        return converted
      }
    } catch {
      return null
    }
  }

  const parsed = new Date(value as string)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function toDashboardPlanSummary(plan: Plan): PlannerDashboardPlanSummary {
  const stageCount = Array.isArray(plan.stages) ? plan.stages.length : 0
  const distance =
    typeof plan.routeData?.distance === 'number'
      ? plan.routeData.distance
      : typeof (plan.routeData as any)?.distanceKm === 'number'
        ? (plan.routeData as any).distanceKm
        : undefined
  const duration =
    typeof plan.routeData?.duration === 'number'
      ? plan.routeData.duration
      : typeof (plan.routeData as any)?.durationMinutes === 'number'
        ? (plan.routeData as any).durationMinutes
        : undefined

  const collaborators = Array.isArray(plan.collaborators) ? plan.collaborators.length : 0
  const sharedWith = Array.isArray(plan.sharedWith) ? plan.sharedWith.length : 0
  const permissions = plan.sharedPermissions ? Object.keys(plan.sharedPermissions).length : 0

  return {
    id: plan.id,
    name: plan.name,
    description: plan.description,
    isPublic: plan.isPublic || plan.visibility === 'public',
    stageCount,
    distanceKm: typeof distance === 'number' ? Number(distance.toFixed(2)) : undefined,
    durationMinutes: typeof duration === 'number' ? Math.round(duration) : undefined,
    collaborators: collaborators + sharedWith + permissions,
    updatedAt: normalizeDate(plan.updatedAt)?.toISOString(),
    aiGenerated: Boolean(plan.aiMetadata)
  }
}

function getPlanTemporalBounds(plan: Plan): { start: Date | null; end: Date | null } {
  const stages = Array.isArray(plan.stages) ? plan.stages : []
  const startCandidates: Date[] = []
  const endCandidates: Date[] = []

  for (const stage of stages) {
    const arrival = normalizeDate(stage.estimatedArrival)
    const departure = normalizeDate(stage.estimatedDeparture)

    if (arrival) {
      startCandidates.push(arrival)
    }
    if (departure) {
      endCandidates.push(departure)
    }
  }

  const start = startCandidates.length ? new Date(Math.min(...startCandidates.map(date => date.getTime()))) : null
  const end = endCandidates.length ? new Date(Math.max(...endCandidates.map(date => date.getTime()))) : null

  return { start, end }
}
